import { apiFetch } from "@/lib/jwt-auth-client";
import { buildAdArtifactDisplayName, safeFilenameBase } from "../lib/mediaAssetNaming";
import { assertProductScope, withProductScope } from "../data/productArtifactScope";
import type { BananImageResult, KlingVideoResult } from "./adsUtils";

type AdArtifactType = "image" | "video";

/** Structured lineage field persisted on every ad artifact so the
 *  Research → Image → Video chain is queryable in one hop. */
export interface PipelineMetadata {
  sourceType: "research" | "banan" | "kling";
  parentArtifactId?: string;
  researchAngle?: string;
  productId?: string;
}

function buildPipelineMetadata(
  adType: AdArtifactType,
  productId: string,
  extra?: Record<string, unknown>,
): PipelineMetadata {
  const sourceType: PipelineMetadata["sourceType"] = adType === "video" ? "kling" : "banan";

  return {
    sourceType,
    ...(extra?.sourceImageJobId ? { parentArtifactId: extra.sourceImageJobId as string } : {}),
    ...(extra?.sourceResearchAngle ? { researchAngle: extra.sourceResearchAngle as string } : {}),
    productId,
  };
}

const SUBJECT_MAP: Record<AdArtifactType, string> = {
  image: "ads:image",
  video: "ads:video",
};

const MEDIA_TYPE_MAP: Record<AdArtifactType, string> = {
  image: "image",
  video: "video",
};

function getContentMd(adType: AdArtifactType, result: BananImageResult | KlingVideoResult): string {
  if (adType === "image") return (result as BananImageResult).output ?? "";
  return (result as KlingVideoResult).video_output ?? "";
}

export function persistCampaignArtifact(opts: {
  platform: "google" | "meta";
  campaignName: string;
  result: Record<string, unknown>;
  workspaceId: string;
  /** Optional. ads:campaign is product-scoped in the contract, but the
   *  Meta/Google publish path usually has no product context — strict
   *  enforcement is intentionally deferred for this writer (see
   *  docs/planning/ads-product-scope-unification.md, campaign-writer open
   *  question). Stamped onto metadata when supplied; never asserted here. */
  productId?: string;
}): Promise<Response> {
  const { platform, campaignName, result, workspaceId, productId } = opts;
  const platformLabel = platform === "google" ? "Google Ads" : "Meta Ads";
  const name = `Ad Campaign - ${platformLabel} - ${campaignName}`;

  return apiFetch("/api/artifacts/save/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name,
      subject: "ads:campaign",
      type: "json",
      content_md: JSON.stringify(result),
      metadata: {
        platform,
        campaignName,
        ...result,
        ...(productId ? { productId } : {}),
      },
      workspace_id: workspaceId,
      coagent_type: "ads",
    }),
  });
}

export function persistAdArtifact(opts: {
  adType: AdArtifactType;
  result: BananImageResult | KlingVideoResult;
  workspaceId: string;
  label?: string;
  subject?: string;
  extraMetadata?: Record<string, unknown>;
}): Promise<Response> {
  const { adType, result, workspaceId, label, subject: subjectOverride, extraMetadata } = opts;
  const subject = subjectOverride ?? SUBJECT_MAP[adType];
  const productId =
    typeof extraMetadata?.productId === "string" ? extraMetadata.productId : undefined;
  // Product-scoped subjects fail fast; Zeely video-ads namespaces are not
  // product-scoped and can persist without a selected product.
  assertProductScope(productId, subject);
  const displayName = buildAdArtifactDisplayName({
    adType,
    label: label ?? "Generated",
    metadata: extraMetadata,
  });
  // The backend's /api/artifacts/save/ upserts by (workspace_id, name) when
  // no artifact_id is supplied. buildAdArtifactDisplayName collapses every
  // generation for the same product down to the same pretty label
  // (e.g. "Nike Tennis Classic Prm") — without a per-generation
  // discriminator on `name`, the second Veo / Banan dispatch silently
  // overwrites the first artifact row in place instead of landing as a
  // new tile. Append a short jobId / batch suffix so each generation
  // gets its own row; the readable label stays on `metadata.displayName`
  // and `readableArtifactTitle` keeps using it for the UI.
  const jobId =
    typeof extraMetadata?.jobId === "string" ? extraMetadata.jobId : undefined;
  const batchIndex =
    typeof extraMetadata?.batchIndex === "number" ? extraMetadata.batchIndex : undefined;
  const discriminator =
    jobId !== undefined
      ? batchIndex !== undefined
        ? `${jobId}_${batchIndex}`
        : jobId
      : `${Date.now().toString(36)}`;
  const name = `${displayName} · ${discriminator}`;

  return apiFetch("/api/artifacts/save/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name,
      subject,
      type: MEDIA_TYPE_MAP[adType],
      content_md: getContentMd(adType, result),
      metadata: withProductScope(
        {
          displayName,
          fileBaseName: safeFilenameBase(displayName),
          result,
          pipeline_metadata: buildPipelineMetadata(adType, productId, extraMetadata),
          ...extraMetadata,
        },
        productId,
        subject,
      ),
      workspace_id: workspaceId,
      coagent_type: "ads",
    }),
  });
}
